import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { EmployeeResponse } from "@/types/employee";

// Hook for fetching employees for a specific organization
// API returns complete list - filtering/sorting done client-side
export function useEmployees(organizationId: string | null) {
  const {
    data: employees = [],
    isLoading,
    error
  } = useQuery<EmployeeResponse[]>({
    queryKey: ["/organizations", organizationId, "employees"],
    queryFn: async () => {
      if (!organizationId) throw new Error("Organization ID is required");
      const response = await apiRequest("GET", `/organizations/${organizationId}/employees`);
      return response.data || response;
    },
    enabled: !!organizationId
  });

  return {
    employees,
    isLoading,
    error
  };
}

// Hook for fetching a single employee
export function useEmployee(organizationId: string | null, employeeId: string | null) {
  const {
    data: employee,
    isLoading,
    error
  } = useQuery<EmployeeResponse>({
    queryKey: ["/organizations", organizationId, "employees", employeeId],
    queryFn: async () => {
      if (!organizationId || !employeeId) throw new Error("Organization ID and Employee ID are required");
      const response = await apiRequest("GET", `/organizations/${organizationId}/employees/${employeeId}`);
      return response.data || response;
    },
    enabled: !!organizationId && !!employeeId
  });

  return {
    employee,
    isLoading,
    error
  };
}